import clsx from "clsx";

import {
  useCalendarData,
  useExtendedBasketData,
} from "@chef/state-management/hooks";
import { compareBasketsProducts } from "@chef/state-management/helpers";
import { sumByProperty } from "@chef/utils/array";
import { isMealboxBasketType, isMealboxProduct } from "@chef/helpers";

import type { WeekYear } from "@chef/types/common";

type DeviationIndicatorProps = WeekYear & {
  className?: string;
};

export const DeviationIndicator = ({
  week,
  year,
  className,
}: DeviationIndicatorProps) => {
  const { data: extendedBasket } = useExtendedBasketData({ week, year });

  const { basket, calendar } = useCalendarData({
    week,
    year,
  });

  const calendarWeek = calendar?.find(
    (cw) => cw.week === week && cw.year === year,
  );

  const shippingDetails = calendarWeek?.baskets.find((b) =>
    isMealboxBasketType(b),
  )?.shippingDetails;

  const deliveryInformation = shippingDetails?.plannedDeliveryInformation;

  const isPaused = !shippingDetails?.hasDelivery;
  const isUndeliverable = !deliveryInformation?.isAvailableForDelivery;

  const expectedBasket = [
    ...basket.mealboxBasket,
    ...basket.groceryBasket,
  ].filter((p) => !isMealboxProduct(p));
  const actualBasket = (extendedBasket.selectedProducts ?? []).filter(
    (p) => !isMealboxProduct(p),
  );

  const basketsAreEqual = compareBasketsProducts(expectedBasket, actualBasket);
  const numProducts = sumByProperty(actualBasket, "quantity");

  const indicateBasketChange =
    !basketsAreEqual && !isPaused && !isUndeliverable;

  return (
    <span
      className={clsx(
        "bg-black text-xxs text-white rounded-full p-2 w-4 h-4 flex items-center justify-center",
        indicateBasketChange && "bg-error",
        className,
      )}
    >
      {numProducts}
    </span>
  );
};
